import { useState, useEffect } from "react";
import { activitiesApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ListTodo, DollarSign } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const Activities = () => {
  const { isAdmin } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    default_hourly_rate: "",
  });

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      const response = await activitiesApi.getAll();
      setActivities(response.data);
    } catch (error) {
      toast.error("Kunde inte ladda aktiviteter");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        default_hourly_rate: formData.default_hourly_rate
          ? parseFloat(formData.default_hourly_rate)
          : null,
      };

      if (editingActivity) {
        await activitiesApi.update(editingActivity.id, data);
        toast.success("Aktivitet uppdaterad");
      } else {
        await activitiesApi.create(data);
        toast.success("Aktivitet skapad");
      }

      setDialogOpen(false);
      resetForm();
      loadActivities();
    } catch (error) {
      toast.error("Kunde inte spara aktivitet");
    }
  };

  const handleEdit = (activity) => {
    setEditingActivity(activity);
    setFormData({
      name: activity.name,
      description: activity.description || "",
      default_hourly_rate: activity.default_hourly_rate?.toString() || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (activityId) => {
    if (!confirm("Är du säker på att du vill ta bort denna aktivitet?")) return;

    try {
      await activitiesApi.delete(activityId);
      toast.success("Aktivitet borttagen");
      loadActivities();
    } catch (error) {
      toast.error("Kunde inte ta bort aktivitet");
    }
  };

  const resetForm = () => {
    setEditingActivity(null);
    setFormData({
      name: "",
      description: "",
      default_hourly_rate: "",
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 slide-up" data-testid="activities-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ListTodo className="w-5 h-5 text-[#6dbf4a]" />
            <h1 className="text-2xl font-bold tracking-tight text-white">Aktiviteter</h1>
          </div>
          <p className="text-white/25 text-sm">Hantera arbetstyper och aktiviteter</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNewDialog}
              className="gap-2 btn-hover"
              data-testid="new-activity-btn"
            >
              <Plus className="w-4 h-4" />
              Ny aktivitet
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>
                {editingActivity ? "Redigera aktivitet" : "Skapa ny aktivitet"}
              </DialogTitle>
              <DialogDescription>
                {editingActivity
                  ? "Uppdatera aktivitetens information"
                  : "Fyll i information om den nya aktiviteten"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Namn *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="T.ex. Snickare, Elektriker, Målare"
                  data-testid="activity-name-input"
                  required
                />
              </div>
              {isAdmin && (
                <div className="space-y-2">
                  <Label htmlFor="default_hourly_rate">Timpris (kr/h)</Label>
                  <Input
                    id="default_hourly_rate"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.default_hourly_rate}
                    onChange={(e) =>
                      setFormData({ ...formData, default_hourly_rate: e.target.value })
                    }
                    placeholder="T.ex. 550"
                    data-testid="activity-rate-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Timpriset används automatiskt vid tidrapportering. Om inget sätts används projektets pris.
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="description">Beskrivning</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Beskriv aktiviteten..."
                  data-testid="activity-description-input"
                  rows={3}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Avbryt
                </Button>
                <Button type="submit" data-testid="activity-submit-btn">
                  {editingActivity ? "Spara ändringar" : "Skapa aktivitet"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Activities Table */}
      {activities.length > 0 ? (
        <Card data-testid="activities-table">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Namn</TableHead>
                  <TableHead>Beskrivning</TableHead>
                  {isAdmin && <TableHead className="text-right">Timpris</TableHead>}
                  <TableHead className="text-right">Åtgärder</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activities.map((activity) => (
                  <TableRow key={activity.id} data-testid={`activity-row-${activity.id}`}>
                    <TableCell className="font-medium">{activity.name}</TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">
                      {activity.description || "-"}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        {activity.default_hourly_rate ? (
                          <span className="flex items-center justify-end gap-1">
                            <DollarSign className="w-3 h-3 text-muted-foreground" />
                            {activity.default_hourly_rate.toLocaleString("sv-SE")} kr/h
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Projektets pris</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(activity)}
                          data-testid={`edit-activity-${activity.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(activity.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-activity-${activity.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <ListTodo className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">Inga aktiviteter ännu</h3>
            <p className="text-muted-foreground mb-4">
              Skapa din första aktivitet för att kategorisera arbete
            </p>
            <Button onClick={openNewDialog} className="gap-2">
              <Plus className="w-4 h-4" />
              Skapa aktivitet
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Activities;
